import React, { useEffect, useMemo, useState } from "react";
import { FileText, CheckCircle, Clock, XCircle } from "lucide-react";
import { reportService } from "../api/reportService";
import { getDateRangeForPeriod } from "../utils/salesUtils";

export default function SummaryStats({ appliedRange }) {
  const [transactionStats, setTransactionStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSummaryStats = async () => {
      try {
        setLoading(true);
        setError("");

        let period;
        if (appliedRange?.startMonth && appliedRange?.endMonth) {
          const [startYear, startMonth] = appliedRange.startMonth.split("-").map(Number);
          const [endYear, endMonth] = appliedRange.endMonth.split("-").map(Number);
          const pad = (num) => String(num).padStart(2, "0");
          const lastDay = new Date(endYear, endMonth, 0).getDate();
          period = {
            start_date: `${startYear}-${pad(startMonth)}-01`,
            end_date: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`,
          };
        } else {
          period = getDateRangeForPeriod("month");
        }

        const response = await reportService.getDashboard({
          ...period,
          pending_limit: 10,
        });

        setTransactionStats(response?.data?.transaction_stats || null);
      } catch (err) {
        console.error("Failed to load summary stats:", err);
        setError(err?.message || "Gagal memuat statistik");
      } finally {
        setLoading(false);
      }
    };

    loadSummaryStats();
  }, [appliedRange]);

  const statsData = useMemo(() => {
    const toDisplay = (value) => Number(value || 0).toLocaleString("id-ID");
    return {
      total: toDisplay(transactionStats?.total),
      done: toDisplay(transactionStats?.done),
      pending: toDisplay(transactionStats?.pending),
      cancelled: toDisplay(transactionStats?.cancelled),
    };
  }, [transactionStats]);

  const stats = [
    {
      title: "Total Trx",
      value: statsData.total,
      icon: FileText,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      accent: "bg-blue-500/70",
      ring: "ring-blue-100",
    },
    {
      title: "Trx Selesai",
      value: statsData.done,
      icon: CheckCircle,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      accent: "bg-emerald-500/70",
      ring: "ring-emerald-100",
    },
    {
      title: "Trx Pending",
      value: statsData.pending,
      icon: Clock,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      accent: "bg-amber-500/70",
      ring: "ring-amber-100",
    },
    {
      title: "Trx Dibatalkan",
      value: statsData.cancelled,
      icon: XCircle,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-600",
      accent: "bg-rose-500/70",
      ring: "ring-rose-100",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {loading && (
        <div className="md:col-span-2 text-sm text-slate-500 bg-white ring-1 ring-slate-200/70 rounded-xl px-4 py-3 shadow-sm">
          Memuat statistik transaksi...
        </div>
      )}
      {error && !loading && (
        <div className="md:col-span-2 text-sm text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-xl px-4 py-3">
          {error}
        </div>
      )}
      {stats.map((stat, index) => (
        <div
          key={index}
          className="group relative bg-white rounded-xl p-4 ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-3 h-full overflow-hidden">
          {/* subtle accent bar */}
          <span
            className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${stat.accent}`}
          />

          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${stat.iconBg} ${stat.iconColor} ring-1 ${stat.ring}`}>
            <stat.icon className="w-5 h-5" />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span
              className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap overflow-hidden text-ellipsis"
              title={stat.title}>
              {stat.title}
            </span>
            <span className="text-2xl font-bold text-slate-900 leading-tight tracking-tight">
              {loading ? "—" : stat.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
